import { useEffect, useState } from 'react';
import {
  RECEIPT_PRINTER_FEED_LINES_BEFORE_CUT_MAX,
  RECEIPT_PRINTER_FEED_LINES_BEFORE_CUT_MIN,
  type PaymentTerminalDriver,
  type PeripheralBlock,
  type TerminalPeripheralConfig,
} from '@reform-lite/contracts';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  SOFTFISCAL_PRINTER_MODELS,
  SOFTFISCAL_PRODUCTION_EKA_URL,
  SOFTFISCAL_PRODUCTION_PKI_URL,
} from '@reform-lite/contracts';
import { PERIPHERAL_BLOCK_LABELS } from '@/hooks/use-terminals';
import { useT } from '@/i18n/use-t';

/**
 * RFL-241 — the field set for each hardware block.
 *
 * Deliberately dumb: it renders what the block can hold and reports edits
 * upward. Every validation rule that matters lives in the contract schema and
 * runs server-side on write, because the till fails closed on malformed
 * config and a browser-only check would let a bad value reach the lane
 * whenever someone used the API directly.
 *
 * Empty string means "not authored" throughout — never zero, never a default.
 * That distinction is the whole point of the feature: an unauthored baud lets
 * the till keep its build define, while a `0` would be delivered as a baud of
 * zero.
 */

type BlockValue = Record<string, unknown>;

/** Transports each block may legally use, mirroring the contract. */
const TRANSPORTS: Partial<Record<PeripheralBlock, readonly string[]>> = {
  receiptPrinter: ['serial', 'network', 'usb'],
  barcodeScanner: ['serial', 'keyboardWedge'],
  labelPrinter: ['serial', 'network', 'usb'],
};

const MANAGED_TRANSPORTS = [
  'serial',
  'keyboardWedge',
  'network',
  'usb',
  'localSocket',
  'service',
] as const;

const MANAGED_BLOCKS: readonly PeripheralBlock[] = [
  'fiscalSecurityModuleEndpoint',
  'paymentTerminal',
  'secondPaymentTerminal',
  'fiscalService',
  'baggingScale',
  'attendantCall',
  'authenticationReader',
  'lanService',
];

/**
 * Card-terminal drivers Back Office may name (YUKI-364): every value of the
 * contract's `PaymentTerminalDriver`, with its label key and baked English.
 * Not authored keeps the till's build driver.
 */
const PAYMENT_TERMINAL_DRIVER_LABEL_KEY: Record<PaymentTerminalDriver, readonly [string, string]> =
  {
    'privatbank-ecr': ['terminal.hardware.field.paymentDriverPrivatbank', 'PrivatBank ECR'],
    'pumb-ssi': ['terminal.hardware.field.paymentDriverPumb', 'PUMB (SSI)'],
    'eps-asyncpos': ['terminal.hardware.field.paymentDriverEpsAsyncPos', 'EPS asyncPOS'],
    'eps-softpos-baltics': [
      'terminal.hardware.field.paymentDriverEpsSoftPos',
      'EPS SoftPOS (Android)',
    ],
  };

const NETWORK_BLOCKS: readonly PeripheralBlock[] = ['pinPad', 'fiscalSidecar'];
const SERIAL_BLOCKS: readonly PeripheralBlock[] = ['fiscalSecurityModule'];

/** A starting point when an operator switches a block to "set here". */
export const BLOCK_DEFAULTS: Record<PeripheralBlock, BlockValue> = {
  receiptPrinter: {},
  barcodeScanner: {},
  pinPad: {},
  fiscalSecurityModule: {},
  fiscalSecurityModuleEndpoint: {},
  fiscalSecurityModuleWorkDirectory: {},
  fiscalSidecar: {},
  paymentTerminal: {},
  secondPaymentTerminal: {},
  fiscalService: {},
  baggingScale: {},
  attendantCall: {},
  authenticationReader: {},
  lanService: {},
  labelPrinter: {},
  // RL-957 (owner Q5): a block switched to "set here" starts with the
  // production PKI and i.EKA addresses; the admin may change them. Nothing
  // else is pre-filled, and nothing is filled in when the file is built.
  softFiscalAndroid: { pki: SOFTFISCAL_PRODUCTION_PKI_URL, eka: SOFTFISCAL_PRODUCTION_EKA_URL },
};

function text(value: unknown): string {
  return value === undefined || value === null ? '' : String(value);
}

/** Strip keys the operator left blank, so absence stays absence. */
function prune(value: BlockValue): BlockValue {
  const out: BlockValue = {};
  for (const [k, v] of Object.entries(value)) {
    if (v === undefined || v === null || v === '') continue;
    if (typeof v === 'object') {
      const inner = prune(v as BlockValue);
      if (Object.keys(inner).length > 0) out[k] = inner;
      continue;
    }
    out[k] = v;
  }
  return out;
}

/**
 * RL-889: whether an authored lines-before-cut is one the schema accepts.
 * Blank is valid (not authored); the server stays the authority on write.
 */
function feedLinesBeforeCutIsValid(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= RECEIPT_PRINTER_FEED_LINES_BEFORE_CUT_MIN &&
    value <= RECEIPT_PRINTER_FEED_LINES_BEFORE_CUT_MAX
  );
}

/** Keep invalid input visible and rejectable; never erase a sign or decimal. */
function feedLinesBeforeCutInput(raw: string): number | string | undefined {
  if (raw === '') return undefined;
  if (!/^-?\d+$/.test(raw)) return raw;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : raw;
}

function num(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function PeripheralBlockFields({
  block,
  value,
  disabled,
  onChange,
}: {
  block: PeripheralBlock;
  value: BlockValue;
  disabled: boolean;
  onChange: (next: BlockValue) => void;
}) {
  const t = useT('admin_ui');
  const set = (patch: BlockValue) => onChange(prune({ ...value, ...patch }));
  const nested = (key: string) => (value[key] ?? {}) as BlockValue;
  const setNested = (key: string, patch: BlockValue) =>
    onChange(prune({ ...value, [key]: { ...nested(key), ...patch } }));
  const accessibleFieldName = (fieldKey: string, fallback: string) =>
    `${t(`terminal.hardware.block.${block}`, PERIPHERAL_BLOCK_LABELS[block])}: ${t(fieldKey, fallback)}`;

  const transports = TRANSPORTS[block];

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {transports !== undefined && (
        <Field label={t('terminal.hardware.field.transport', 'Transport')}>
          <Select
            aria-label={accessibleFieldName('terminal.hardware.field.transport', 'Transport')}
            disabled={disabled}
            value={text(value.transport)}
            onChange={(e) => set({ transport: e.target.value === '' ? undefined : e.target.value })}
          >
            <option value="">
              {t('terminal.hardware.field.notAuthored', 'Not authored — till decides')}
            </option>
            {transports.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
      )}

      {block === 'fiscalSecurityModuleWorkDirectory' && (
        // MICRO-56: an absolute path; the contract refuses anything else on write.
        <Field label={t('terminal.hardware.field.workDirectory', 'Work directory')}>
          <Input
            aria-label={accessibleFieldName(
              'terminal.hardware.field.workDirectory',
              'Work directory',
            )}
            disabled={disabled}
            value={text(value.path)}
            placeholder="/var/lib/softfiscal-linux/softfiscal/work"
            onChange={(e) => set({ path: e.target.value })}
          />
        </Field>
      )}

      {block === 'softFiscalAndroid' && (
        <>
          {/*
            RL-957. What SoftFiscal Android needs to reach the security module
            and the printer, and its PKI / i.EKA services. No X8 port field:
            the APK binds 8899, and Back Office does not deliver another port.
          */}
          <Field label={t('terminal.hardware.field.softFiscalSm', 'Security module USB device')}>
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.softFiscalSm',
                'Security module USB device',
              )}
              disabled={disabled}
              value={text(value.sm)}
              placeholder="usb · usb-0403-6015"
              onChange={(e) => set({ sm: e.target.value })}
            />
          </Field>
          <Field
            label={t('terminal.hardware.field.softFiscalPrinter', 'Receipt printer USB device')}
          >
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.softFiscalPrinter',
                'Receipt printer USB device',
              )}
              disabled={disabled}
              value={text(value.printer)}
              placeholder="usb-04b8-0202 · NUL"
              onChange={(e) => set({ printer: e.target.value })}
            />
          </Field>
          <Field label={t('terminal.hardware.field.softFiscalPrinterModel', 'Printer model')}>
            <Select
              aria-label={accessibleFieldName(
                'terminal.hardware.field.softFiscalPrinterModel',
                'Printer model',
              )}
              disabled={disabled}
              value={text(value.printerModel)}
              onChange={(e) =>
                set({ printerModel: e.target.value === '' ? undefined : e.target.value })
              }
            >
              <option value="">
                {t('terminal.hardware.field.notAuthored', 'Not authored — till decides')}
              </option>
              {SOFTFISCAL_PRINTER_MODELS.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('terminal.hardware.field.softFiscalPki', 'PKI address')}>
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.softFiscalPki',
                'PKI address',
              )}
              disabled={disabled}
              value={text(value.pki)}
              placeholder={SOFTFISCAL_PRODUCTION_PKI_URL}
              onChange={(e) => set({ pki: e.target.value })}
            />
          </Field>
          <Field label={t('terminal.hardware.field.softFiscalEka', 'i.EKA address')}>
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.softFiscalEka',
                'i.EKA address',
              )}
              disabled={disabled}
              value={text(value.eka)}
              placeholder={SOFTFISCAL_PRODUCTION_EKA_URL}
              onChange={(e) => set({ eka: e.target.value })}
            />
          </Field>
        </>
      )}
      {block === 'labelPrinter' && (
        <>
          <Field
            label={t('terminal.hardware.field.printerPort', 'Port (serial or USB device path)')}
          >
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.printerPort',
                'Port (serial or USB device path)',
              )}
              disabled={disabled}
              value={text(nested('serial').port)}
              placeholder="/dev/usb/lp0 · COM8"
              onChange={(e) => setNested('serial', { port: e.target.value })}
            />
          </Field>
          <Field label={t('terminal.hardware.field.serialBaud', 'Baud (serial only)')}>
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.serialBaud',
                'Baud (serial only)',
              )}
              disabled={disabled}
              inputMode="numeric"
              value={text(nested('serial').baud)}
              placeholder="115200"
              onChange={(e) => setNested('serial', { baud: num(e.target.value) })}
            />
          </Field>
          <Field label={t('terminal.hardware.field.printerHost', 'Host (network printers)')}>
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.printerHost',
                'Host (network printers)',
              )}
              disabled={disabled}
              value={text(nested('network').host)}
              onChange={(e) => setNested('network', { host: e.target.value })}
            />
          </Field>
          <Field label={t('terminal.hardware.field.networkPort', 'Network port')}>
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.networkPort',
                'Network port',
              )}
              disabled={disabled}
              inputMode="numeric"
              value={text(nested('network').port)}
              placeholder="9100"
              onChange={(e) => setNested('network', { port: num(e.target.value) })}
            />
          </Field>
          <Field
            label={t('terminal.hardware.field.columns', 'Columns — 32 on 58 mm paper, 48 on 80 mm')}
          >
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.columns',
                'Columns — 32 on 58 mm paper, 48 on 80 mm',
              )}
              disabled={disabled}
              inputMode="numeric"
              value={text(value.columns)}
              onChange={(e) => set({ columns: num(e.target.value) })}
            />
          </Field>
        </>
      )}
      {block === 'receiptPrinter' && (
        <>
          {/*
            RL-749. Who drives the printer. Not authored keeps the till's own
            rule (a port here means the till prints; none means the fiscal
            service does). "Fiscal service" tells the till never to open the
            printer device, even with a port authored below: a printer device
            has one owner, and a till that takes it leaves the fiscal service
            without a printer.
          */}
          <Field
            label={t(
              'terminal.hardware.field.printerOwner',
              'Who prints — the terminal, or the fiscal service on this machine',
            )}
          >
            <Select
              aria-label={accessibleFieldName(
                'terminal.hardware.field.printerOwner',
                'Who prints — the terminal, or the fiscal service on this machine',
              )}
              disabled={disabled}
              value={text(value.owner)}
              onChange={(e) => set({ owner: e.target.value === '' ? undefined : e.target.value })}
            >
              <option value="">
                {t('terminal.hardware.field.notAuthored', 'Not authored — till decides')}
              </option>
              <option value="terminal">
                {t('terminals.peripheral.ownerTerminal', 'The terminal prints')}
              </option>
              <option value="fiscalService">
                {t(
                  'terminals.peripheral.ownerFiscalService',
                  'The fiscal service prints — the terminal never opens the printer',
                )}
              </option>
            </Select>
          </Field>
          <Field
            label={t('terminal.hardware.field.printerPort', 'Port (serial or USB device path)')}
          >
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.printerPort',
                'Port (serial or USB device path)',
              )}
              disabled={disabled}
              value={text(nested('serial').port)}
              placeholder="/dev/usb/lp0 · COM8"
              onChange={(e) => setNested('serial', { port: e.target.value })}
            />
          </Field>
          <Field label={t('terminal.hardware.field.serialBaud', 'Baud (serial only)')}>
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.serialBaud',
                'Baud (serial only)',
              )}
              disabled={disabled}
              inputMode="numeric"
              value={text(nested('serial').baud)}
              placeholder="115200"
              onChange={(e) => setNested('serial', { baud: num(e.target.value) })}
            />
          </Field>
          <Field label={t('terminal.hardware.field.printerHost', 'Host (network printers)')}>
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.printerHost',
                'Host (network printers)',
              )}
              disabled={disabled}
              value={text(nested('network').host)}
              onChange={(e) => setNested('network', { host: e.target.value })}
            />
          </Field>
          <Field label={t('terminal.hardware.field.networkPort', 'Network port')}>
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.networkPort',
                'Network port',
              )}
              disabled={disabled}
              inputMode="numeric"
              value={text(nested('network').port)}
              placeholder="9100"
              onChange={(e) => setNested('network', { port: num(e.target.value) })}
            />
          </Field>
          <Field
            label={t('terminal.hardware.field.columns', 'Columns — 32 on 58 mm paper, 48 on 80 mm')}
          >
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.columns',
                'Columns — 32 on 58 mm paper, 48 on 80 mm',
              )}
              disabled={disabled}
              inputMode="numeric"
              value={text(value.columns)}
              onChange={(e) => set({ columns: num(e.target.value) })}
            />
          </Field>
          <Field
            label={t(
              'terminal.hardware.field.codepage',
              'Codepage — wrong value prints Cyrillic as ?',
            )}
          >
            <Select
              aria-label={accessibleFieldName(
                'terminal.hardware.field.codepage',
                'Codepage — wrong value prints Cyrillic as ?',
              )}
              disabled={disabled}
              value={text(value.codepage)}
              onChange={(e) =>
                set({ codepage: e.target.value === '' ? undefined : e.target.value })
              }
            >
              <option value="">
                {t('terminal.hardware.field.notAuthored', 'Not authored — till decides')}
              </option>
              <option value="ascii">ascii</option>
              <option value="cp1251">
                {t('terminals.peripheral.codepageCp1251', 'cp1251 — Cyrillic')}
              </option>
              <option value="cp866">
                {t('terminals.peripheral.codepageCp866', 'cp866 — Cyrillic DOS')}
              </option>
              <option value="cp1257">
                {t('terminals.peripheral.codepageCp1257', 'cp1257 — Baltic')}
              </option>
            </Select>
          </Field>
          {/*
            VL-346. The printer's own number for the codepage above (ESC t n).
            The codepage decides how the till encodes text; this decides which
            page the printer is switched to, and only the model knows it.
            Blank is not 0: blank keeps the till's choice (no ESC t at all for
            cp1251), while 0 is PC437 and would turn Cyrillic into Latin.
          */}
          <Field
            label={t(
              'terminal.hardware.field.codepageTable',
              'Codepage table number (ESC t) — Epson: 46 for cp1251, 17 for cp866',
            )}
          >
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.codepageTable',
                'Codepage table number (ESC t) — Epson: 46 for cp1251, 17 for cp866',
              )}
              disabled={disabled}
              inputMode="numeric"
              placeholder={t('terminal.hardware.field.notAuthoredShort', 'Not authored')}
              value={text(value.codepageTable)}
              onChange={(e) => set({ codepageTable: num(e.target.value) })}
            />
          </Field>
          <Field label={t('terminal.hardware.field.printerCut', 'Cut after printing')}>
            <Select
              aria-label={accessibleFieldName(
                'terminal.hardware.field.printerCut',
                'Cut after printing',
              )}
              disabled={disabled}
              value={text(value.cut)}
              onChange={(e) => set({ cut: e.target.value === '' ? undefined : e.target.value })}
            >
              {/* Not authored keeps the full cut every till has printed with. */}
              <option value="">
                {t('terminal.hardware.field.notAuthored', 'Not authored — till decides')}
              </option>
              <option value="full">{t('terminals.peripheral.cutFull', 'Full cut')}</option>
              <option value="partial">
                {t('terminals.peripheral.cutPartial', 'Partial cut — leaves a tab')}
              </option>
              <option value="none">
                {t('terminals.peripheral.cutNone', 'No cut — the printer has no cutter')}
              </option>
            </Select>
          </Field>
          {/*
            RL-889. Blank lines fed before the cut, so the blade falls below
            the last printed line. Blank is not 0: blank keeps the till's own
            3 lines (today's bytes), 0 feeds nothing. The schema refuses
            anything outside 0-10 on write; the hint says so before Save.
          */}
          <Field
            label={t('terminal.hardware.field.feedLinesBeforeCut', 'Lines before cut (blank = 3)')}
          >
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.feedLinesBeforeCut',
                'Lines before cut (blank = 3)',
              )}
              disabled={disabled}
              inputMode="numeric"
              placeholder={t('terminal.hardware.field.notAuthoredShort', 'Not authored')}
              value={text(value.feedLinesBeforeCut)}
              onChange={(e) => set({ feedLinesBeforeCut: feedLinesBeforeCutInput(e.target.value) })}
            />
            {!feedLinesBeforeCutIsValid(value.feedLinesBeforeCut) && (
              <p className="text-xs text-destructive">
                {t(
                  'terminal.hardware.field.feedLinesBeforeCutInvalid',
                  'Lines before cut must be a whole number from 0 to 10',
                )}
              </p>
            )}
          </Field>
          <Field
            label={t(
              'terminal.hardware.field.printerFont',
              'Font — B prints smaller and shortens the receipt',
            )}
          >
            <Select
              aria-label={accessibleFieldName(
                'terminal.hardware.field.printerFont',
                'Font — B prints smaller and shortens the receipt',
              )}
              disabled={disabled}
              value={text(value.font)}
              onChange={(e) => set({ font: e.target.value === '' ? undefined : e.target.value })}
            >
              {/*
                "Not authored" is a real third state, not a placeholder for A.
                The till resolves synced config -> build define -> printer
                default, so sending 'a' here would silently outrank a lane built
                with --dart-define=REFORM_PRINTER_FONT=b.
              */}
              <option value="">
                {t('terminal.hardware.field.notAuthored', 'Not authored — till decides')}
              </option>
              <option value="a">
                {t('terminals.peripheral.fontA', 'a — 12x24, printer default')}
              </option>
              <option value="b">{t('terminals.peripheral.fontB', 'b — 9x17, smaller')}</option>
            </Select>
          </Field>
          <Field
            label={t(
              'terminal.hardware.field.printerCashDrawer',
              'Cash drawer wired through the printer DK port',
            )}
          >
            <Select
              aria-label={accessibleFieldName(
                'terminal.hardware.field.printerCashDrawer',
                'Cash drawer wired through the printer DK port',
              )}
              disabled={disabled}
              value={text(value.cashDrawer)}
              onChange={(e) =>
                set({ cashDrawer: e.target.value === '' ? undefined : e.target.value === 'true' })
              }
            >
              {/*
                Three states, not a checkbox. The till resolves synced config ->
                build define -> off, and an authored value outranks the lane's
                --printer-drawer flag — so "no" must be sayable separately from
                "nobody has said", or every unconfigured terminal would switch
                off a lane that was built with a drawer.
              */}
              <option value="">
                {t('terminal.hardware.field.notAuthored', 'Not authored — till decides')}
              </option>
              <option value="true">
                {t('terminals.peripheral.drawerYes', 'Yes — a drawer is connected')}
              </option>
              <option value="false">
                {t('terminals.peripheral.drawerNo', 'No — nothing on the DK port')}
              </option>
            </Select>
          </Field>
          {/*
            YUKI-278. Two-character keys naming logos already uploaded to the
            printer's non-volatile memory, not images. Nothing on the wire can
            tell the till which keys a printer holds, so these are authored
            like the paper width. Left blank the till falls through to its
            build define, which is how every Yuki lane prints one today.
          */}
          <Field
            label={t(
              'terminal.hardware.field.printerLogoKeycode',
              'Header logo key — two characters, stored in the printer',
            )}
          >
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.printerLogoKeycode',
                'Header logo key — two characters, stored in the printer',
              )}
              disabled={disabled}
              maxLength={2}
              placeholder={t('terminal.hardware.field.notAuthoredShort', 'Not authored')}
              value={text(value.logoKeycode)}
              onChange={(e) =>
                set({ logoKeycode: e.target.value === '' ? undefined : e.target.value })
              }
            />
          </Field>
          <Field
            label={t(
              'terminal.hardware.field.printerMarkingLogoKeycode',
              'Fiscal marking logo key — printed beside the ФН line',
            )}
          >
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.printerMarkingLogoKeycode',
                'Fiscal marking logo key — printed beside the ФН line',
              )}
              disabled={disabled}
              maxLength={2}
              placeholder={t('terminal.hardware.field.notAuthoredShort', 'Not authored')}
              value={text(value.markingLogoKeycode)}
              onChange={(e) =>
                set({ markingLogoKeycode: e.target.value === '' ? undefined : e.target.value })
              }
            />
          </Field>
        </>
      )}

      {block === 'barcodeScanner' && (
        <>
          <Field
            label={t(
              'terminal.hardware.field.scannerPort',
              'Port (serial scanners only — a wedge needs none)',
            )}
          >
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.scannerPort',
                'Port (serial scanners only — a wedge needs none)',
              )}
              disabled={disabled}
              value={text(nested('serial').port)}
              placeholder="/dev/ttyUSB0 · COM3"
              onChange={(e) => setNested('serial', { port: e.target.value })}
            />
          </Field>
          <Field label={t('terminal.hardware.field.baud', 'Baud')}>
            <Input
              aria-label={accessibleFieldName('terminal.hardware.field.baud', 'Baud')}
              disabled={disabled}
              inputMode="numeric"
              value={text(nested('serial').baud)}
              placeholder="9600"
              onChange={(e) => setNested('serial', { baud: num(e.target.value) })}
            />
          </Field>
          <Field label={t('terminal.hardware.field.minBarcodeLength', 'Minimum barcode length')}>
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.minBarcodeLength',
                'Minimum barcode length',
              )}
              disabled={disabled}
              inputMode="numeric"
              value={text(value.minBarcodeLength)}
              onChange={(e) => set({ minBarcodeLength: num(e.target.value) })}
            />
          </Field>
        </>
      )}

      {NETWORK_BLOCKS.includes(block) && (
        <>
          <Field label={t('terminal.hardware.field.host', 'Host')}>
            <Input
              aria-label={accessibleFieldName('terminal.hardware.field.host', 'Host')}
              disabled={disabled}
              value={text(value.host)}
              onChange={(e) => set({ host: e.target.value })}
            />
          </Field>
          <Field label={t('terminal.hardware.field.port', 'Port')}>
            <Input
              aria-label={accessibleFieldName('terminal.hardware.field.port', 'Port')}
              disabled={disabled}
              inputMode="numeric"
              value={text(value.port)}
              onChange={(e) => set({ port: num(e.target.value) })}
            />
          </Field>
        </>
      )}

      {SERIAL_BLOCKS.includes(block) && (
        <>
          <Field label={t('terminal.hardware.field.port', 'Port')}>
            <Input
              aria-label={accessibleFieldName('terminal.hardware.field.port', 'Port')}
              disabled={disabled}
              value={text(value.port)}
              onChange={(e) => set({ port: e.target.value })}
            />
          </Field>
          <Field label={t('terminal.hardware.field.baud', 'Baud')}>
            <Input
              aria-label={accessibleFieldName('terminal.hardware.field.baud', 'Baud')}
              disabled={disabled}
              inputMode="numeric"
              value={text(value.baud)}
              onChange={(e) => set({ baud: num(e.target.value) })}
            />
          </Field>
        </>
      )}

      {block === 'baggingScale' && (
        <BaggingScaleFields
          value={value}
          disabled={disabled}
          set={set}
          replace={(next) => onChange(prune(next))}
          label={accessibleFieldName}
        />
      )}

      {MANAGED_BLOCKS.includes(block) && !(block === 'baggingScale' && value.fitted === false) && (
        <>
          {block === 'paymentTerminal' && (
            <Field label={t('terminal.hardware.field.paymentDriver', 'Card terminal driver')}>
              <Select
                aria-label={accessibleFieldName(
                  'terminal.hardware.field.paymentDriver',
                  'Card terminal driver',
                )}
                disabled={disabled}
                value={text(value.driver)}
                onChange={(e) =>
                  set({ driver: e.target.value === '' ? undefined : e.target.value })
                }
              >
                <option value="">
                  {t(
                    'terminal.hardware.field.paymentDriverBuild',
                    'Not authored — the build’s driver',
                  )}
                </option>
                {Object.entries(PAYMENT_TERMINAL_DRIVER_LABEL_KEY).map(
                  ([driver, [key, english]]) => (
                    <option key={driver} value={driver}>
                      {t(key, english)}
                    </option>
                  ),
                )}
              </Select>
            </Field>
          )}
          <Field label={t('terminal.hardware.field.transport', 'Transport')}>
            <Select
              aria-label={accessibleFieldName('terminal.hardware.field.transport', 'Transport')}
              disabled={disabled}
              value={text(value.transport)}
              onChange={(e) =>
                set({ transport: e.target.value === '' ? undefined : e.target.value })
              }
            >
              <option value="">
                {t('terminal.hardware.field.notAuthored', 'Not authored — till decides')}
              </option>
              {MANAGED_TRANSPORTS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label={t('terminal.hardware.field.address', 'Address — port, socket path or host')}
          >
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.address',
                'Address — port, socket path or host',
              )}
              disabled={disabled}
              value={text(value.address)}
              onChange={(e) => set({ address: e.target.value })}
            />
          </Field>
          <Field label={t('terminal.hardware.field.endpointBaud', 'Baud (serial endpoints only)')}>
            <Input
              aria-label={accessibleFieldName(
                'terminal.hardware.field.endpointBaud',
                'Baud (serial endpoints only)',
              )}
              disabled={disabled}
              inputMode="numeric"
              value={text(value.baud)}
              onChange={(e) => set({ baud: num(e.target.value) })}
            />
          </Field>
        </>
      )}
    </div>
  );
}

/**
 * SSC-116 — what only a person can say about the SCO security scale: that
 * there is none, which driver it is, its declared division and the Shekel's
 * serial number. The endpoint itself (transport, address, baud) is the
 * managed block's field set, rendered after this.
 */
function BaggingScaleFields({
  value,
  disabled,
  set,
  replace,
  label,
}: {
  value: BlockValue;
  disabled: boolean;
  set: (patch: BlockValue) => void;
  replace: (next: BlockValue) => void;
  label: (fieldKey: string, fallback: string) => string;
}) {
  const t = useT('admin_ui');
  const fittedLabel = t('terminal.hardware.field.scaleFitted', 'Security scale fitted');
  return (
    <>
      <Field label={fittedLabel}>
        <Select
          aria-label={label('terminal.hardware.field.scaleFitted', 'Security scale fitted')}
          disabled={disabled}
          value={text(value.fitted)}
          onChange={(e) => {
            const raw = e.target.value;
            // "No" is a whole-block statement: a lane with no scale has no
            // driver, port or division, and the contract refuses them.
            if (raw === 'false') replace({ fitted: false });
            else set({ fitted: raw === '' ? undefined : true });
          }}
        >
          {/*
            Three states, like the cash drawer. Blank lets the till keep its
            build define; "No" must be sayable on its own, or a lane with no
            scale could only be told so by a rebuild.
          */}
          <option value="">
            {t('terminal.hardware.field.notAuthored', 'Not authored — till decides')}
          </option>
          <option value="true">
            {t('terminal.hardware.option.scaleFittedYes', 'Yes — a security scale is fitted')}
          </option>
          <option value="false">
            {t('terminal.hardware.option.scaleFittedNo', 'No — this lane has no security scale')}
          </option>
        </Select>
      </Field>
      {value.fitted !== false && (
        <>
          <Field label={t('terminal.hardware.field.scaleModel', 'Scale model (driver)')}>
            <Select
              aria-label={label('terminal.hardware.field.scaleModel', 'Scale model (driver)')}
              disabled={disabled}
              value={text(value.model)}
              onChange={(e) => set({ model: e.target.value === '' ? undefined : e.target.value })}
            >
              <option value="">
                {t(
                  'terminal.hardware.option.scaleModelInferred',
                  'Not authored — inferred from the transport',
                )}
              </option>
              <option value="shekel">
                {t('terminal.hardware.option.scaleShekel', 'Shekel — serial security scale')}
              </option>
              <option value="digi">
                {t('terminal.hardware.option.scaleDigi', 'DIGI — through ScaleModule')}
              </option>
              <option value="simulated">
                {t('terminal.hardware.option.scaleSimulated', 'Simulated — demo builds only')}
              </option>
            </Select>
          </Field>
          <Field
            label={t('terminal.hardware.field.scaleDivision', 'Division d, grams (0.5 allowed)')}
          >
            <DecimalInput
              ariaLabel={label(
                'terminal.hardware.field.scaleDivision',
                'Division d, grams (0.5 allowed)',
              )}
              disabled={disabled}
              value={value.divisionG}
              placeholder="2"
              onChange={(divisionG) => set({ divisionG })}
            />
          </Field>
          <Field
            label={t('terminal.hardware.field.scaleSerialNumber', 'Serial number (Shekel only)')}
          >
            <Input
              aria-label={label(
                'terminal.hardware.field.scaleSerialNumber',
                'Serial number (Shekel only)',
              )}
              disabled={disabled}
              value={text(value.serialNumber)}
              onChange={(e) => set({ serialNumber: e.target.value })}
            />
          </Field>
        </>
      )}
    </>
  );
}

/**
 * A number field that may hold a fraction. Keeps what the operator typed
 * (`0.`, `0,5`) while it is not yet a number, instead of snapping it back to
 * the last parsed value, and reports only a finite number or "not authored".
 */
function DecimalInput({
  ariaLabel,
  disabled,
  value,
  placeholder,
  onChange,
}: {
  ariaLabel: string;
  disabled: boolean;
  value: unknown;
  placeholder?: string;
  onChange: (next: number | undefined) => void;
}) {
  const [draft, setDraft] = useState(text(value));
  useEffect(() => {
    setDraft((current) => (parseDecimal(current) === value ? current : text(value)));
  }, [value]);
  return (
    <Input
      aria-label={ariaLabel}
      disabled={disabled}
      inputMode="decimal"
      value={draft}
      placeholder={placeholder}
      onChange={(e) => {
        setDraft(e.target.value);
        onChange(parseDecimal(e.target.value));
      }}
    />
  );
}

function parseDecimal(raw: string): number | undefined {
  return num(raw.replace(',', '.'));
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

/** One-line summary of a block, for the collapsed row. */
export function summarisePeripheralBlock(
  block: PeripheralBlock,
  config: TerminalPeripheralConfig,
  translate: (key: string, bakedEnglish?: string) => string = (_key, bakedEnglish) =>
    bakedEnglish ?? _key,
): string {
  const value = config[block] as BlockValue | undefined;
  if (value === undefined)
    return translate('terminal.hardware.summary.notAuthored', 'not authored');
  const parts: string[] = [];
  const push = (label: string, v: unknown) => {
    if (v !== undefined && v !== null && v !== '') parts.push(`${label} ${String(v)}`);
  };
  push('', value.transport);
  const serial = (value.serial ?? {}) as BlockValue;
  const network = (value.network ?? {}) as BlockValue;
  push(
    translate('terminal.hardware.summary.port', 'port'),
    serial.port ?? value.port ?? value.address,
  );
  push(translate('terminal.hardware.summary.baud', 'baud'), serial.baud ?? value.baud);
  push(translate('terminal.hardware.summary.host', 'host'), network.host ?? value.host);
  if (value.owner === 'fiscalService' || value.owner === 'terminal') {
    parts.push(
      value.owner === 'fiscalService'
        ? translate('terminal.hardware.summary.ownerFiscalService', 'fiscal service prints')
        : translate('terminal.hardware.summary.ownerTerminal', 'terminal prints'),
    );
  }
  push(translate('terminal.hardware.summary.columns', 'columns'), value.columns);
  push(translate('terminal.hardware.summary.codepage', 'codepage'), value.codepage);
  push(translate('terminal.hardware.summary.codepageTable', 'ESC t'), value.codepageTable);
  push(translate('terminal.hardware.summary.cut', 'cut'), value.cut);
  push(
    translate('terminal.hardware.summary.feedLinesBeforeCut', 'lines before cut'),
    value.feedLinesBeforeCut,
  );
  push(translate('terminal.hardware.summary.font', 'font'), value.font);
  // Explicit rather than via push(): `false` is a meaningful authored value
  // here, and push() renders it as the string "false" only by accident of
  // String() — spelling it out keeps the summary readable and intentional.
  if (value.cashDrawer !== undefined && value.cashDrawer !== null) {
    parts.push(
      `${translate('terminal.hardware.summary.cashDrawer', 'cash drawer')} ${
        value.cashDrawer === true
          ? translate('terminal.hardware.summary.yes', 'yes')
          : translate('terminal.hardware.summary.no', 'no')
      }`,
    );
  }
  push(translate('terminal.hardware.summary.minLength', 'min length'), value.minBarcodeLength);
  push(translate('terminal.hardware.summary.model', 'model'), value.model);
  push(translate('terminal.hardware.summary.division', 'd (g)'), value.divisionG);
  push(translate('terminal.hardware.summary.serialNumber', 'serial'), value.serialNumber);
  if (value.fitted === false) {
    parts.push(translate('terminal.hardware.summary.noScale', 'no security scale fitted'));
  } else if (value.fitted === true) {
    parts.push(translate('terminal.hardware.summary.scaleFitted', 'security scale fitted'));
  }
  push(translate('terminal.hardware.summary.driver', 'driver'), value.driver);
  // MICRO-56: the security-module work directory.
  push(translate('terminal.hardware.summary.directory', 'directory'), value.path);
  // RL-957: the SoftFiscal Android block.
  push(translate('terminal.hardware.summary.sm', 'SM'), value.sm);
  push(translate('terminal.hardware.summary.printer', 'printer'), value.printer);
  push(translate('terminal.hardware.summary.model', 'model'), value.printerModel);
  push(translate('terminal.hardware.summary.pki', 'PKI'), value.pki);
  push(translate('terminal.hardware.summary.eka', 'i.EKA'), value.eka);
  return parts.length === 0
    ? translate('terminal.hardware.summary.empty', 'authored, no values')
    : parts.join(' · ').trim();
}
